import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
    CompositeFieldType,
    Entity,
    UILayout,
    UIElementType,
    Description,
} from '#types/System.types';
import { CUSTOM_COMPONENTS_LIMIT, RecordStatus } from '#types/System.Parameters.types';
import { useLayoutEditorBase } from '../../use/editor/useLayoutEditorBase';
import { useGet } from '../../use/data/useGet';
import { useAdd } from '../../use/data/useAdd';
import { useRemove } from '../../use/data/useRemove';
import { useSet } from '../../use/data/useSet';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import { invalidateCompositeFieldTypeCache } from './compositeFieldTypeCache';
import { createCollectionStore } from '../../store';

const compositeFieldTypesStore = createCollectionStore<CompositeFieldType>('compositeFieldTypes', {
    queryOptions: { extraConstraints: [{ type: 'limit', n: CUSTOM_COMPONENTS_LIMIT }] },
});
const entitiesStore = createCollectionStore<Entity>('entities');

/**
 * Layout editor hook for composite field types: CRUD, structure selection, and preview fields.
 */
export const useLayoutEditorCompositeField = () => {
    const [selectedStructureId, setSelectedStructureId] = useState<string | undefined>();
    const [structure, setStructure] = useState<CompositeFieldType | null>(null);
    const [previewFieldIds, setPreviewFieldIds] = useState<string[]>([]);
    const [previewFieldIdsDirty, setPreviewFieldIdsDirty] = useState(false);
    const { get } = useGet();
    const { add } = useAdd();
    const { remove } = useRemove();
    const { set } = useSet();
    const { setCustomFieldTypes, deleteFieldType } = useContext(FieldTypeContext);

    useEffect(() => {
        compositeFieldTypesStore.connect();
        entitiesStore.connect();
        return () => {
            compositeFieldTypesStore.disconnect();
            entitiesStore.disconnect();
        };
    }, []);

    const compositeSnap = useSnapshot(compositeFieldTypesStore.state);
    const structures = useMemo<CompositeFieldType[]>(() => {
        const result: CompositeFieldType[] = [];
        for (const id of compositeSnap.docIds) {
            const entry = compositeSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as CompositeFieldType), docId: id });
        }
        return result;
    }, [compositeSnap.docIds, compositeSnap.docs]);

    const entitiesSnap = useSnapshot(entitiesStore.state);
    const entities = useMemo<Entity[]>(() => {
        const result: Entity[] = [];
        for (const id of entitiesSnap.docIds) {
            const entry = entitiesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as Entity), docId: id });
        }
        return result;
    }, [entitiesSnap.docIds, entitiesSnap.docs]);

    const handleSave = useCallback(async (layout: UILayout, description: Description) => {
        if (!structure || !selectedStructureId) {
            throw new Error('No structure selected');
        }

        const updates: Omit<CompositeFieldType, 'docId'> = {
            defaultUILayout: (
                ({ meta, ...rest }) => rest)(layout as UILayout & { meta?: any }
            ),
            description,
            status: structure.status,
            previewFieldIds,
        };

        await set('compositeFieldTypes', selectedStructureId, updates);
        invalidateCompositeFieldTypeCache();

        setCustomFieldTypes({
            fields: {
                [selectedStructureId]: {
                    fieldDataTypeId: 'composite',
                    description,
                },
            },
        });

        setPreviewFieldIdsDirty(false);
    }, [set, structure, selectedStructureId, setCustomFieldTypes, previewFieldIds]);

    const baseEditor = useLayoutEditorBase({
        onSave: handleSave,
    });

    const { dispatch } = baseEditor;

    const handleDescriptionChange = (key: keyof Description, value: string) => {
        dispatch({
            type: 'UPDATE_DESCRIPTION',
            payload: { [key]: value },
        });
    };

    const handlePreviewFieldIdsChange = useCallback((ids: string[]) => {
        setPreviewFieldIds(ids);
        setPreviewFieldIdsDirty(true);
    }, []);

    const selectStructure = useCallback(async (structureId: string) => {
        const fetched = await get<CompositeFieldType>('compositeFieldTypes', structureId);
        if (!fetched) return false;

        setSelectedStructureId(structureId);
        setStructure(fetched);
        setPreviewFieldIds(fetched.previewFieldIds ?? []);
        setPreviewFieldIdsDirty(false);
        dispatch({
            type: 'SELECT_STRUCTURE',
            payload: {
                layout: fetched.defaultUILayout,
                description: fetched.description,
            },
        });
        return true;
    }, [get, dispatch]);

    const createStructure = useCallback(async () => {
        const newCompositeFieldType: Omit<CompositeFieldType, 'docId'> = {
            defaultUILayout: {
                structure: {
                    uiElementType: UIElementType.Column,
                    children: [],
                },
            },
            description: {
                shortLabel: 'New Composite Field',
                shortDescription: '',
                longLabel: '',
                longDescription: 'Recently created Composite Field, please update this description.',
            },
            status: RecordStatus.Active,
        };

        const newId = await add('compositeFieldTypes', newCompositeFieldType);
        invalidateCompositeFieldTypeCache();

        setCustomFieldTypes({
            fields: {
                [newId]: {
                    fieldDataTypeId: 'composite',
                    description: newCompositeFieldType.description,
                },
            },
        });

        setSelectedStructureId(newId);
        setStructure({ ...newCompositeFieldType, docId: newId });
        setPreviewFieldIds([]);
        setPreviewFieldIdsDirty(false);
        dispatch({
            type: 'SELECT_STRUCTURE',
            payload: {
                layout: newCompositeFieldType.defaultUILayout,
                description: newCompositeFieldType.description,
            },
        });
        return newId;
    }, [add, dispatch, setCustomFieldTypes]);

    const deleteStructure = useCallback(async (structureId: string) => {
        await remove('compositeFieldTypes', structureId);
        invalidateCompositeFieldTypeCache();
        await deleteFieldType(structureId);
        setSelectedStructureId(undefined);
        setStructure(null);
        setPreviewFieldIds([]);
        setPreviewFieldIdsDirty(false);
        dispatch({ type: 'RESET_LAYOUT' });
        return true;
    }, [remove, dispatch, deleteFieldType]);

    const clearSelection = () => {
        setSelectedStructureId(undefined);
        setStructure(null);
        setPreviewFieldIds([]);
        setPreviewFieldIdsDirty(false);
        dispatch({ type: 'RESET_LAYOUT' });
    };

    const duplicateStructure = useCallback(async (newName: string) => {
        if (!structure || !selectedStructureId) {
            throw new Error('No structure selected to duplicate');
        }

        const duplicated: Omit<CompositeFieldType, 'docId'> = {
            defaultUILayout: (
                ({ meta, ...rest }) => rest)(structure.defaultUILayout as UILayout & { meta?: any }
            ),
            description: {
                ...structure.description,
                shortLabel: newName,
                longDescription: `${newName} (Copy of ${structure.description?.shortLabel || 'Untitled'})`,
            },
            status: RecordStatus.Active,
            previewFieldIds,
        };

        const newId = await add('compositeFieldTypes', duplicated);
        invalidateCompositeFieldTypeCache();

        setCustomFieldTypes({
            fields: {
                [newId]: {
                    fieldDataTypeId: 'composite',
                    description: duplicated.description,
                },
            },
        });

        return newId;
    }, [structure, selectedStructureId, add, setCustomFieldTypes, previewFieldIds]);

    return {
        ...baseEditor,
        isModified: baseEditor.isModified || previewFieldIdsDirty,
        structure,
        structures,
        entities,
        previewFieldIds,
        createStructure,
        deleteStructure,
        duplicateStructure,
        selectStructure,
        selectedStructureId,
        clearSelection,
        handleDescriptionChange,
        handlePreviewFieldIdsChange,
    };
};
